import { track } from "@amplitude/analytics-react-native";
import { useApolloClient, useQuery } from "@apollo/client";
import { FlashList } from "@shopify/flash-list";
import { MutableRefObject, useEffect, useMemo } from "react";
import GetCommentThreadDocument from "src/api/graphql/__generated__/documents/GetCommentThreadDocument";
import GetCommentThreadForPollDocument from "src/api/graphql/__generated__/documents/GetCommentThreadForPollDocument";
import OptimisticCommentThreadFragmentFragmentDoc from "src/api/graphql/__generated__/documents/OptimisticCommentThreadFragmentFragmentDoc";
import { CommentFragmentFragment } from "src/api/graphql/__generated__/graphql";
import { MIN_TO_MS } from "src/helpers/constants";
import { useClientId } from "src/helpers/globalHelpers";
import parseV0TemplateUrl from "src/helpers/parseV0TemplateUrl";
import useStabilizedCallback from "src/hooks/useStabilizedCallback";
import useStateRef from "src/hooks/useStateRef";
import { Timestamp_MS } from "src/shared/types/general";
import { CommentAttachmentType } from "../components/CommentBubble";
import createRenderCommentContextMenu from "../helpers/createRenderCommentContextMenu";
import { CommentsThreadRowType } from "../types";
import useCommentsThreadStore from "./useCommentsThreadStore";
import useNativePreparedData from "./useNativePreparedData";

const NewGroupDurationBreak_MS = 5 * MIN_TO_MS;
const useCommentsThreadData = ({
	listRef,
}: {
	listRef: MutableRefObject<FlashList<any> | null>;
}) => {
	const displayMessageThreads = useCommentsThreadStore((store) => store.displayMessageThreads);
	const threadSpecifier = useCommentsThreadStore((store) => store.threadSpecifier);
	const getOptimisticKeyLookup = useCommentsThreadStore((store) => store.getOptimisticKeyLookup);
	const topic = useCommentsThreadStore((store) => store.topic);
	const optimisticCommentLookup = useCommentsThreadStore(
		(store) => store.optimisticCommentLookup,
	);
	const isAdmin = useCommentsThreadStore((store) => store.isAdmin);
	const { data, fetchMore, loading, refetch } = useQuery(GetCommentThreadDocument, {
		variables: {
			input: threadSpecifier,
			nestedCommentThreadInput: {
				minDepth: 2,
				noop: !displayMessageThreads,
			},
			commentsInput: {
				limit: 128,
			},
		},
		fetchPolicy: "cache-and-network",
	});
	const mostRecentComment = useMemo(() => {
		let mostRecent: CommentFragmentFragment | null = null;
		for (const comment of data?.getCommentThread?.comments?.payload ?? []) {
			if (!mostRecent || (comment.postedAt ?? 0) > (mostRecent.postedAt ?? 0)) {
				mostRecent = comment;
			}
		}
		return mostRecent;
	}, [data?.getCommentThread?.comments?.payload]);
	const { data: longPollData } = useQuery(GetCommentThreadForPollDocument, {
		variables: {
			input: threadSpecifier,
			nestedCommentThreadInput: {
				minDepth: 2,
				noop: !displayMessageThreads,
			},
			commentsInput: {
				lastCommentId: mostRecentComment?.commentId ?? null,
				lastPostedAt: mostRecentComment?.postedAt ?? null,
			},
		},
		fetchPolicy: "network-only",
		// Only start once we've retrieved the first batch of comments
		skip: !data?.getCommentThread?.comments?.payload && !loading,
		context: {
			pinBehavior: "long",
		},
		pollInterval: 500,
	});
	const apolloClient = useApolloClient();
	useEffect(() => {
		const longPollCommentsResult = longPollData?.getCommentThread?.longPollComments;
		if (longPollCommentsResult?.forceFullRefresh) {
			topic.emit("onNewCommentsPolled", {});
			refetch();
		} else if (longPollData?.getCommentThread) {
			const newComments = longPollCommentsResult?.comments ?? [];
			if (newComments.length > 0) {
				topic.emit("onNewCommentsPolled", {});
				// Optimistically update the cache with the new comments
				apolloClient.writeFragment({
					id: apolloClient.cache.identify({
						__typename: "CommentThread",
						threadSpecifier: longPollData.getCommentThread.threadSpecifier,
					}),
					fragment: OptimisticCommentThreadFragmentFragmentDoc,
					fragmentName: "OptimisticCommentThreadFragment",
					data: {
						__typename: "CommentThread",
						threadSpecifier: longPollData.getCommentThread.threadSpecifier,
						comments: {
							__typename: "CommentsCursor",
							payload: newComments,
						},
					},
				});
			}
		}
	}, [longPollData, apolloClient, refetch, topic]);

	const [isFetchingMore, setIsFetchingMore, isFetchingMoreRef] = useStateRef(false);
	const handleFetchMore = useStabilizedCallback(async () => {
		const cursor = data?.getCommentThread?.comments?.cursor;
		if (!cursor) return;
		if (cursor === "{}") return;
		if (isFetchingMoreRef.current) return;
		setIsFetchingMore(true);
		try {
			await fetchMore({
				variables: {
					input: threadSpecifier,
					commentsInput: {
						limit: 64,
						cursor,
					},
				},
			});
		} finally {
			setIsFetchingMore(false);
		}
	}, [
		data?.getCommentThread?.comments?.cursor,
		fetchMore,
		isFetchingMoreRef,
		setIsFetchingMore,
		threadSpecifier,
	]);

	const clientId = useClientId();
	const { listData } = useMemo<{ listData: CommentsThreadRowType.OneOf[] }>(() => {
		const listData: CommentsThreadRowType.OneOf[] = [];
		const comments: (CommentFragmentFragment & {
			isOptimistic?: boolean;
		})[] = [];
		const seenCommentIds = new Set<string>();
		const commentThread = data?.getCommentThread;
		for (const comment of commentThread?.comments?.payload ?? []) {
			if (seenCommentIds.has(comment.commentId)) continue;
			seenCommentIds.add(comment.commentId);
			comments.push(comment);
		}
		for (const optimisticComment of Object.values(optimisticCommentLookup)) {
			if (seenCommentIds.has(optimisticComment.commentId)) continue;
			seenCommentIds.add(optimisticComment.commentId);
			comments.push({
				...optimisticComment,
				isOptimistic: true,
			});
		}

		const sortedComments = comments.sort((a, b) => {
			return (a.postedAt ?? 0) - (b.postedAt ?? 0);
		});
		const seenPhotoIds = new Set<string>();

		if (commentThread?.photoHeader && commentThread.comments?.cursor === "{}") {
			const imageUrl = parseV0TemplateUrl({
				readUrl: commentThread.photoHeader.readUrl,
				aspectRatio: "1x1",
				size: "sm",
			});
			if (imageUrl && commentThread.photoHeader.gatheringId) {
				seenPhotoIds.add(commentThread.photoHeader.photoId);
				listData.push({
					variant: "PhotoHeader",
					itemKey: `photoHeader-${commentThread.photoHeader.photoId}`,
					photoHeader: {
						imageUrl,
						href: `/album/${commentThread.photoHeader.gatheringId}/photo/${commentThread.photoHeader.photoId}`,
						ownerId: commentThread.photoHeader.ownerId,
						photoId: commentThread.photoHeader.photoId,
					},
				});
			}
		}

		let prev: {
			isRemoved: boolean;
			postedAt: Timestamp_MS;
			creatorUserId: string | null;
			threadReplyId: string | null;
			isFirstUnread?: boolean;
		} | null = null;

		const commentsWithPositions: (CommentFragmentFragment & {
			isOptimistic?: boolean;
			position: "Top" | "Middle" | "Bottom";
			isGroupStart?: boolean;
			isGroupEnd?: boolean;
			isFirstPhotoOccurrence?: boolean;
			isFirstUnread?: boolean;
		})[] = [];
		let hasUnreadIndicator = false;
		// We need to do two passes to determine position first then populate attachments
		for (const comment of sortedComments) {
			if (!comment.postedAt) continue;

			const isFirstUnread =
				// Guarding against loading to ensure better optimistic UI
				!loading &&
				!hasUnreadIndicator &&
				comment.postedAt > (commentThread?.lastViewedAt ?? 0) &&
				// Own comments are never considered unread
				comment.creatorUserId !== clientId;
			if (isFirstUnread) {
				hasUnreadIndicator = true;
			}

			/**
			 * Boolean to determine if this comment should start a new group of messages
			 */
			const isNewGroup =
				!prev ||
				// If the previous comment is more than x ms ago, start a new group
				comment.postedAt - prev.postedAt > NewGroupDurationBreak_MS ||
				// Any non-user comments always create a new group
				!comment.creatorUserId ||
				// If the previous comment is from a different user, start a new group
				(comment.creatorUserId ?? null) !== prev.creatorUserId ||
				// If the previous comment is from a different photo header, start a new group
				((!!comment.threadReplyId || !!prev.threadReplyId) &&
					(comment.threadReplyId ?? null) !== prev.threadReplyId) ||
				// The first unread comment should always start a new group
				isFirstUnread;

			if (isNewGroup) {
				const prevListItem = commentsWithPositions[commentsWithPositions.length - 1];
				if (prevListItem) {
					prevListItem.isGroupEnd = true;
					if (prevListItem.position === "Middle") {
						prevListItem.position = "Bottom";
					}
				}
			}

			commentsWithPositions.push({
				...comment,
				position: isNewGroup ? "Top" : "Middle",
				isGroupStart: isNewGroup,
				isFirstPhotoOccurrence:
					!!comment.commentThread?.photoHeader &&
					!seenPhotoIds.has(comment.commentThread.photoHeader.photoId),
				isFirstUnread,
			});
			if (comment.commentThread?.photoHeader?.photoId) {
				seenPhotoIds.add(comment.commentThread.photoHeader.photoId);
			}

			prev = {
				isRemoved: !comment.content,
				postedAt: comment.postedAt,
				creatorUserId: comment.creatorUserId ?? null,
				threadReplyId: comment.threadReplyId ?? null,
				isFirstUnread,
			};
		}
		const prevListItem = commentsWithPositions[commentsWithPositions.length - 1];
		if (prevListItem) {
			prevListItem.isGroupEnd = true;
			if (prevListItem.position === "Middle") {
				prevListItem.position = "Bottom";
			}
		}

		const optimisticKeyLookup = getOptimisticKeyLookup();
		for (const comment of commentsWithPositions) {
			if (!comment.postedAt) continue;
			const sender = comment.creatorUserId === clientId ? "Self" : "Other";

			const attachments: CommentAttachmentType.OneOf[] = [];
			attachments.push({
				variant: "ContextMenu",
				renderContextMenu: createRenderCommentContextMenu({
					isSelf: comment.creatorUserId === clientId,
					commentId: comment.commentId,
					isAdmin,
				}),
			});

			// Name
			if (comment.isGroupStart && sender === "Other") {
				attachments.push({
					variant: "Name",
					name:
						comment.otherUserCreator?.firstName && comment.otherUserCreator?.lastName
							? `${comment.otherUserCreator.firstName} ${comment.otherUserCreator.lastName}`
							: "Removed User",
				});
			}

			// Profile picture
			if (comment.isGroupEnd && sender === "Other") {
				attachments.push({
					variant: "ProfilePicture",
					userId: comment.otherUserCreator?.userId,
				});
			}

			if (
				comment.isGroupEnd &&
				displayMessageThreads &&
				comment.commentThread?.estimatedNumCommentsLessThan100 != null &&
				comment.commentThread.estimatedNumCommentsLessThan100 > 1 &&
				// Only photo replies support now
				comment.commentThread.photoHeader?.photoId
			) {
				const photoId = comment.commentThread.photoHeader.photoId;
				const count = comment.commentThread.estimatedNumCommentsLessThan100;
				attachments.push({
					variant: "ReplyCount",
					count,
					onPress: () => {
						track("comment_threadReplyCount_press", {
							count,
							photoId,
						});
						topic.emit("onPhotoThreadPress", {
							photoId,
						});
					},
				});
			}

			// Thread preview
			if (comment.isGroupStart && displayMessageThreads && comment.content) {
				if (comment.commentThread?.photoHeader) {
					const imageUrl = parseV0TemplateUrl({
						readUrl: comment.commentThread?.photoHeader.readUrl,
					});
					const photoHeader = comment.commentThread.photoHeader;
					if (imageUrl && photoHeader) {
						attachments.push({
							variant: comment.isFirstPhotoOccurrence ? "LargeImage" : "SmallImage",
							imageUrl,
							onPress: () => {
								topic.emit("onPhotoThreadPress", {
									photoId: photoHeader.photoId,
								});
							},
						});
					}
				} else if (comment.threadReplyId) {
					attachments.push({
						variant: "DeletedThread",
					});
				}
			}
			if (comment.isFirstUnread) {
				listData.push({
					variant: "UnreadIndicator",
					itemKey: "UnreadIndicator",
				});
			}

			listData.push({
				variant: "CommentBubble",
				// Trying to maintain the same key from the optimistic comment
				itemKey: optimisticKeyLookup[comment.commentId] ?? comment.commentId,
				commentBubble: {
					content: comment.content ?? null,
					sender,
					position: comment.position,
					isSending: comment.isOptimistic ?? false,
					attachments,
				},
			});
		}
		if (listData.length === 0 && !loading) {
			listData.push({
				variant: "Empty",
				itemKey: "Empty",
			});
		}

		return {
			// Inverted flash list requires a double reverse
			listData: listData.reverse(),
		};
	}, [
		data,
		clientId,
		optimisticCommentLookup,
		isAdmin,
		displayMessageThreads,
		topic,
		loading,
		getOptimisticKeyLookup,
	]);

	const preparedData = useNativePreparedData(listData, listRef);

	return {
		listData: preparedData,
		loading: loading || isFetchingMore,
		fetchMore: handleFetchMore,
	};
};
export default useCommentsThreadData;
